// ============================================================================
// SirUsersPage.jsx — SUPER ADMIN ONLY.
//
// The roster of people using the public /sir search, and the one place their
// limit is raised. A "user" here is an OTP-VERIFIED MOBILE NUMBER: that is the
// identity the meter charges and credits attach to. Anonymous visitors are a
// device fingerprint with no name, listed on the second tab: they cannot be
// granted anything directly, but they are who rings up saying "my 5 are over",
// so they have to be findable.
//
// ⚠️ The SuperAdmin check in this file only decides whether the page is DRAWN.
// Authority lives on the server: GetSirUsers and AddSirCredits both re-read
// Users.SuperAdmin for the calling UserId and refuse everyone else, so a
// localStorage edit or a hand-rolled POST buys nothing.
//
// Granting is additive and permanent — credits never expire and a grant is
// recorded in SIRCreditPurchase with GrantedBy, so every top-up is traceable to
// the admin who made it. See docs/sir-monetisation.md.
// ============================================================================

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { FetchFromServer } from "../../api/ServerApi";
import { SIR_API, SIR_ASSEMBLY_NAME, SIR_THEME } from "./sirConfig";

const sirCall = (apiName, payload) =>
  FetchFromServer(apiName, payload, { plain: true, open: true });

/** Grant sizes an admin actually uses; anything else goes in the box. */
const QUICK_CREDITS = [10, 25, 50, 100];

export default function SirUsersPage() {
  const navigate = useNavigate();

  const userId = Number(localStorage.getItem("UserId")) || 0;
  const isSuperAdmin = localStorage.getItem("SuperAdmin") === "1";

  const [rows, setRows] = useState([]);
  const [devices, setDevices] = useState([]);
  // Anonymous devices are the majority until people start verifying, so open
  // on whichever tab actually has rows.
  const [tab, setTab] = useState("users");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [search, setSearch] = useState("");
  const [limits, setLimits] = useState({ otpLimit: 5, freeLimit: 5 });

  // Which row's grant panel is open, and what is typed into it.
  const [grantFor, setGrantFor] = useState(null);
  const [grantCredits, setGrantCredits] = useState("");
  const [grantNote, setGrantNote] = useState("");
  const [grantBusy, setGrantBusy] = useState(false);

  // Pre-loading a number a caller reads out over the phone. Credits attach to
  // the NUMBER, not to a session, so granting before they have ever verified
  // works: the balance is waiting the moment their OTP goes through.
  const [manualMobile, setManualMobile] = useState("");
  const [manualCredits, setManualCredits] = useState("");

  // The credit LEDGER: every grant ever made, not a per-number balance. A
  // balance cannot tell you that 100 credits were granted twice by two
  // different admins, which is exactly what an audit needs to see.
  const [grants, setGrants] = useState([]);
  const [grantTotals, setGrantTotals] = useState(null);
  const [grantsLoaded, setGrantsLoaded] = useState(false);

  // Nobody but a super admin gets even the shell of this page.
  useEffect(() => {
    if (!isSuperAdmin || !userId) navigate("/sir", { replace: true });
  }, [isSuperAdmin, userId, navigate]);

  /* -------- Escape goes back to the search page --------
   * Closes the open grant panel first: one keypress should never undo more
   * than the operator can see, and half-typed credits vanishing because they
   * wanted to shut a sub-panel would be its own bug.
   *
   * Bound to window rather than a container so it works wherever the caret is,
   * including inside the search and credit inputs.
   */
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      if (grantFor) {
        setGrantFor(null);
        return;
      }
      navigate("/sir");
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [grantFor, navigate]);

  const load = useCallback(
    async (term = "") => {
      setLoading(true);
      setError("");
      try {
        const res = await sirCall(SIR_API.users, {
          UserId: userId,
          search: term.trim(),
          limit: 500,
        });
        const body = Array.isArray(res) ? res[0] : res;

        if (body?.status !== "ok") {
          setRows([]);
          setDevices([]);
          setError(body?.message || "Could not load the user list.");
          return;
        }

        const users = Array.isArray(body.users) ? body.users : [];
        const anon = Array.isArray(body.devices) ? body.devices : [];
        setRows(users);
        setDevices(anon);
        if (!users.length && anon.length) setTab("devices");
        setLimits({
          otpLimit: Number(body.otpLimit) || 5,
          freeLimit: Number(body.freeLimit) || 5,
        });
      } catch (err) {
        console.error("GetSirUsers failed", err);
        setRows([]);
        setDevices([]);
        setError(
          "Could not reach the server. If this is a fresh deploy, GetSirUsers may not be live yet."
        );
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  const loadGrants = useCallback(
    async (term = "") => {
      setLoading(true);
      setError("");
      try {
        const res = await sirCall(SIR_API.creditGrants, {
          UserId: userId,
          search: term.trim(),
          limit: 500,
        });
        const body = Array.isArray(res) ? res[0] : res;
        if (body?.status !== "ok") {
          setGrants([]);
          setGrantTotals(null);
          setError(body?.message || "Could not load the credit ledger.");
          return;
        }
        setGrants(Array.isArray(body.grants) ? body.grants : []);
        setGrantTotals(body.totals || null);
        setGrantsLoaded(true);
      } catch (err) {
        console.error("GetSirCreditGrants failed", err);
        setGrants([]);
        setGrantTotals(null);
        setError(
          "Could not reach the server. If this is a fresh deploy, GetSirCreditGrants may not be live yet."
        );
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    if (isSuperAdmin && userId) load("");
  }, [isSuperAdmin, userId, load]);

  // Fetched on first view rather than up front: most visits to this page are
  // about who is searching, not about the credit ledger.
  useEffect(() => {
    if (tab === "credits" && !grantsLoaded) loadGrants(search);
  }, [tab, grantsLoaded, loadGrants, search]);

  const openGrant = (row) => {
    setGrantFor(row.MobileNumber);
    setGrantCredits("");
    setGrantNote("");
    setNotice("");
  };

  const grant = useCallback(
    async (mobile, credits) => {
      const n = Math.floor(Number(credits) || 0);
      if (!(n > 0)) {
        setError("Enter how many credits to add.");
        return;
      }

      setGrantBusy(true);
      setError("");
      try {
        const res = await sirCall(SIR_API.addCredits, {
          MobileNumber: mobile,
          Credits: n,
          Source: "admin",
          Note: grantNote.trim() || null,
          UserId: userId,
        });
        const body = Array.isArray(res) ? res[0] : res;

        if (body?.status !== "ok") {
          setError(body?.message || "Could not add credits.");
          return;
        }

        // The server returns the authoritative balance — patch the row with
        // that rather than adding locally, so the two can never drift.
        setRows((prev) =>
          prev.map((r) =>
            r.MobileNumber === mobile
              ? {
                  ...r,
                  CreditsLeft: Number(body.credits) || 0,
                  CreditsBought: (Number(r.CreditsBought) || 0) + n,
                }
              : r
          )
        );
        setNotice(`Added ${n} credits to ${mobile}. Balance is now ${body.credits}.`);
        // The ledger is cached until first viewed; a new grant invalidates it
        // so the Credits tab cannot show a list that is missing what was just
        // granted from this very screen.
        setGrantsLoaded(false);
        setGrantFor(null);
      } catch (err) {
        console.error("AddSirCredits failed", err);
        setError("Could not reach the server to add credits.");
      } finally {
        setGrantBusy(false);
      }
    },
    [grantNote, userId]
  );

  // Grant to a number typed by hand — the phone-call case. The number need not
  // have verified yet; the balance simply waits for it.
  const grantManual = useCallback(async () => {
    const m = manualMobile.replace(/\D/g, "");
    if (m.length !== 10) {
      setError("Enter a valid 10-digit mobile number.");
      return;
    }
    await grant(m, manualCredits);
    setManualMobile("");
    setManualCredits("");
    load(search);
  }, [manualMobile, manualCredits, grant, load, search]);

  if (!isSuperAdmin || !userId) return null;

  const controlStyle = {
    height: 28,
    padding: "2px 8px",
    borderRadius: 6,
    border: "1px solid #ccd4df",
    fontSize: 12.5,
    outline: "none",
  };

  const th = {
    position: "sticky",
    top: 0,
    zIndex: 2,
    background: "#eef3fa",
    color: "#33475b",
    textAlign: "left",
    padding: "4px 8px",
    borderBottom: "2px solid #ccd4df",
    whiteSpace: "nowrap",
    fontWeight: 700,
  };
  const td = { padding: "3px 8px", whiteSpace: "nowrap" };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        overflow: "hidden",
        background: "#fff",
      }}
    >
      {/* ===================== HEADER ===================== */}
      <div
        style={{
          background: `linear-gradient(90deg, ${SIR_THEME.accent}, ${SIR_THEME.bar})`,
          color: SIR_THEME.barText,
          padding: "5px 10px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          boxShadow: "0 1px 4px rgba(13,71,161,0.25)",
        }}
      >
        <div style={{ fontWeight: 800, fontSize: 14, whiteSpace: "nowrap" }}>
          👤 SIR Users — Super Admin
        </div>
        <div
          style={{
            background: "rgba(255,255,255,0.18)",
            border: "1px solid rgba(255,255,255,0.35)",
            padding: "1px 9px",
            borderRadius: 11,
            fontSize: 11.5,
            fontWeight: 700,
            whiteSpace: "nowrap",
          }}
        >
          AC {SIR_ASSEMBLY_NAME}
        </div>
        <div style={{ flex: 1 }} />
        <span style={{ fontSize: 11.5, opacity: 0.9, whiteSpace: "nowrap" }}>
          {rows.length} user{rows.length === 1 ? "" : "s"}
        </span>
        <button
          type="button"
          onClick={() => navigate("/sir")}
          style={{
            ...controlStyle,
            border: "1px solid rgba(255,255,255,0.6)",
            background: "transparent",
            color: "#fff",
            fontWeight: 600,
            cursor: "pointer",
          }}
        >
          ← Search
        </button>
      </div>

      {/* ===================== SEARCH ===================== */}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          // Drive the tab that is actually on screen; otherwise the box above
          // the credit ledger appears to do nothing.
          if (tab === "credits") loadGrants(search);
          else load(search);
        }}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "6px 10px",
          background: "#f7f9fc",
          borderBottom: "1px solid #dfe4ea",
          flexWrap: "wrap",
        }}
      >
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or mobile number…"
          style={{ ...controlStyle, flex: "1 1 260px", maxWidth: 380 }}
        />
        <button
          type="submit"
          disabled={loading}
          style={{
            ...controlStyle,
            background: loading ? "#9bb8d8" : SIR_THEME.bar,
            color: "#fff",
            border: "none",
            fontWeight: 700,
            cursor: loading ? "wait" : "pointer",
            minWidth: 88,
          }}
        >
          {loading ? "Loading…" : "Search"}
        </button>
        <button
          type="button"
          onClick={() => {
            setSearch("");
            if (tab === "credits") loadGrants("");
            else load("");
          }}
          style={{ ...controlStyle, background: "#f1f3f5", cursor: "pointer" }}
        >
          Reset
        </button>
        <span style={{ fontSize: 11.5, color: "#5a6672" }}>
          Free {limits.freeLimit} · OTP {limits.otpLimit} · then credits
        </span>
      </form>

      {/* ============ GRANT TO A NUMBER READ OUT ON THE PHONE ============ */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 6,
          padding: "5px 10px",
          background: "#eef3fa",
          borderBottom: "1px solid #e3e8ef",
          flexWrap: "wrap",
          fontSize: 12,
        }}
      >
        <span style={{ fontWeight: 700, color: "#33475b" }}>
          Add credits to a number:
        </span>
        <input
          value={manualMobile}
          onChange={(e) => setManualMobile(e.target.value.replace(/\D/g, "").slice(0, 10))}
          inputMode="numeric"
          placeholder="10 digit mobile"
          style={{ ...controlStyle, width: 150, letterSpacing: 1, fontWeight: 700 }}
        />
        <input
          value={manualCredits}
          onChange={(e) => setManualCredits(e.target.value.replace(/\D/g, "").slice(0, 6))}
          inputMode="numeric"
          placeholder="Credits"
          style={{ ...controlStyle, width: 90 }}
        />
        <input
          value={grantNote}
          onChange={(e) => setGrantNote(e.target.value.slice(0, 200))}
          placeholder="Note (optional)"
          style={{ ...controlStyle, flex: "1 1 200px", maxWidth: 300 }}
        />
        <button
          type="button"
          disabled={grantBusy || manualMobile.length !== 10 || !manualCredits}
          onClick={grantManual}
          style={{
            ...controlStyle,
            background:
              grantBusy || manualMobile.length !== 10 || !manualCredits ? "#9bb8d8" : "#146c43",
            color: "#fff",
            border: "none",
            fontWeight: 700,
            cursor:
              grantBusy || manualMobile.length !== 10 || !manualCredits
                ? "not-allowed"
                : "pointer",
          }}
          title="Works even before that number has verified — the credits wait for it"
        >
          {grantBusy ? "Adding…" : "Add"}
        </button>
        <span style={{ color: "#5a6672" }}>
          works before they verify — the balance is waiting when their OTP goes through
        </span>
      </div>

      {/* ===================== TABS ===================== */}
      <div
        style={{
          display: "flex",
          gap: 4,
          padding: "5px 10px 0",
          borderBottom: "1px solid #e3e8ef",
        }}
      >
        {[
          { key: "users", label: `Verified users (${rows.length})` },
          { key: "devices", label: `Anonymous devices (${devices.length})` },
          {
            key: "credits",
            label: `Credits allotted${grantsLoaded ? ` (${grants.length})` : ""}`,
          },
        ].map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            style={{
              ...controlStyle,
              borderBottom: "none",
              borderRadius: "6px 6px 0 0",
              background: tab === t.key ? SIR_THEME.bar : "#f1f3f5",
              color: tab === t.key ? "#fff" : "#33475b",
              fontWeight: 700,
              cursor: "pointer",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {(error || notice) && (
        <div
          style={{
            margin: "6px 10px 0",
            padding: "6px 10px",
            borderRadius: 6,
            fontSize: 12.5,
            fontWeight: 600,
            background: error ? "#fdecea" : "#e7f5ec",
            color: error ? "#b3261e" : "#146c43",
            border: `1px solid ${error ? "#f5c2bd" : "#a9d8bd"}`,
          }}
        >
          {error || notice}
        </div>
      )}

      {/* ===================== TABLE ===================== */}
      <div style={{ flex: 1, overflow: "auto", padding: 8, minHeight: 0 }}>
        {!loading && !error && tab === "users" && rows.length === 0 && (
          <div style={{ padding: 28, textAlign: "center", color: "#7b8794", fontSize: 13 }}>
            Nobody has verified a mobile number yet — everyone so far is on the free tier.
            <br />
            See <b>Anonymous devices</b> for who is actually searching.
          </div>
        )}

        {!loading && !error && tab === "devices" && devices.length === 0 && (
          <div style={{ padding: 28, textAlign: "center", color: "#7b8794", fontSize: 13 }}>
            No searches recorded yet.
          </div>
        )}

        {tab === "users" && rows.length > 0 && (
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              overflow: "auto",
              border: "1px solid #e3e8ef",
              boxShadow: "0 1px 3px rgba(16,42,73,0.08)",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={th}>Mobile</th>
                  <th style={th}>Name</th>
                  <th style={th}>Free</th>
                  <th style={th}>OTP left</th>
                  <th style={th}>Credits left</th>
                  <th style={th}>Opened</th>
                  <th style={th}>Devices</th>
                  <th style={th}>Last seen</th>
                  <th style={th}>Limit</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, i) => {
                  const open = grantFor === r.MobileNumber;
                  // Out of everything: the row an admin is looking for.
                  const dry = r.OtpRemaining === 0 && r.CreditsLeft === 0;
                  return (
                    <React.Fragment key={r.MobileNumber}>
                      <tr
                        style={{
                          borderBottom: "1px solid #eef1f5",
                          background: open
                            ? "#e3f0ff"
                            : i % 2
                            ? "#fafbfd"
                            : "#fff",
                        }}
                      >
                        <td style={{ ...td, fontWeight: 700 }}>{r.MobileNumber}</td>
                        <td style={{ ...td, whiteSpace: "normal" }}>
                          {r.Name || <span style={{ color: "#9aa5b1" }}>—</span>}
                          {r.CampaignUserId ? (
                            <span style={{ color: "#5a6672", fontSize: 11 }}>
                              {" "}
                              (staff #{r.CampaignUserId})
                            </span>
                          ) : null}
                        </td>
                        <td style={td}>{r.FreeUsed}</td>
                        <td style={td}>{r.OtpRemaining}</td>
                        <td
                          style={{
                            ...td,
                            fontWeight: 800,
                            color: dry ? "#b3261e" : r.CreditsLeft > 0 ? "#146c43" : "#33475b",
                          }}
                        >
                          {r.CreditsLeft}
                          {r.CreditsBought ? (
                            <span style={{ color: "#8a97a6", fontWeight: 500 }}>
                              {" "}
                              / {r.CreditsBought}
                            </span>
                          ) : null}
                        </td>
                        <td style={td}>{r.TotalOpened}</td>
                        <td style={td}>{r.Devices}</td>
                        <td style={{ ...td, color: "#5a6672" }}>{r.LastSeen || "-"}</td>
                        <td style={td}>
                          <button
                            type="button"
                            onClick={() => (open ? setGrantFor(null) : openGrant(r))}
                            style={{
                              ...controlStyle,
                              height: 24,
                              background: open ? "#33475b" : SIR_THEME.bar,
                              color: "#fff",
                              border: "none",
                              fontWeight: 700,
                              cursor: "pointer",
                            }}
                            title="Add credits to this number"
                          >
                            {open ? "Close" : "+ Credits"}
                          </button>
                        </td>
                      </tr>

                      {open && (
                        <tr style={{ background: "#f2f7ff" }}>
                          <td colSpan={9} style={{ padding: "6px 8px" }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 6,
                                flexWrap: "wrap",
                              }}
                            >
                              <span style={{ fontWeight: 700, color: "#33475b" }}>
                                Add credits to {r.MobileNumber}:
                              </span>

                              {QUICK_CREDITS.map((n) => (
                                <button
                                  key={n}
                                  type="button"
                                  disabled={grantBusy}
                                  onClick={() => grant(r.MobileNumber, n)}
                                  style={{
                                    ...controlStyle,
                                    height: 24,
                                    cursor: grantBusy ? "wait" : "pointer",
                                    fontWeight: 700,
                                    background: "#fff",
                                  }}
                                >
                                  +{n}
                                </button>
                              ))}

                              <input
                                value={grantCredits}
                                onChange={(e) =>
                                  setGrantCredits(e.target.value.replace(/\D/g, "").slice(0, 6))
                                }
                                inputMode="numeric"
                                placeholder="Other"
                                style={{ ...controlStyle, height: 24, width: 80 }}
                              />
                              <input
                                value={grantNote}
                                onChange={(e) => setGrantNote(e.target.value.slice(0, 200))}
                                placeholder="Note (optional) — e.g. paid by UPI"
                                style={{ ...controlStyle, height: 24, flex: "1 1 220px", maxWidth: 320 }}
                              />
                              <button
                                type="button"
                                disabled={grantBusy || !grantCredits}
                                onClick={() => grant(r.MobileNumber, grantCredits)}
                                style={{
                                  ...controlStyle,
                                  height: 24,
                                  background: grantBusy || !grantCredits ? "#9bb8d8" : "#146c43",
                                  color: "#fff",
                                  border: "none",
                                  fontWeight: 700,
                                  cursor: grantBusy || !grantCredits ? "not-allowed" : "pointer",
                                }}
                              >
                                {grantBusy ? "Adding…" : "Add"}
                              </button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {/* ============ CREDITS ALLOTTED ============
          * The grant LEDGER, one row per grant, newest first. Deliberately not
          * a per-number summary: the roster already shows balances, and a
          * balance cannot show that the same number was topped up twice, by
          * whom, or why.
          *
          * "Used" is per NUMBER, not per grant — credits are fungible, so a
          * spend cannot be attributed to one particular top-up. The column is
          * labelled accordingly rather than implying an attribution that does
          * not exist.
          */}
        {tab === "credits" && (
          <>
            {grantTotals && (
              <div
                style={{
                  display: "flex",
                  gap: 14,
                  flexWrap: "wrap",
                  padding: "8px 10px",
                  marginBottom: 8,
                  background: "#f7f9fc",
                  border: "1px solid #e3e8ef",
                  borderRadius: 8,
                  fontSize: 12.5,
                }}
              >
                <span>
                  Grants: <b>{grantTotals.grants}</b>
                </span>
                <span>
                  Numbers: <b>{grantTotals.numbers}</b>
                </span>
                <span>
                  Credits given: <b>{grantTotals.creditsGranted}</b>
                </span>
                <span>
                  Used: <b>{grantTotals.creditsUsed}</b>
                </span>
                <span>
                  Outstanding: <b style={{ color: "#1b5e20" }}>{grantTotals.creditsLeft}</b>
                </span>
              </div>
            )}

            {!loading && grants.length === 0 && (
              <p style={{ fontSize: 13, color: "#5a6672" }}>
                No credits have been given to anyone yet. Use{" "}
                <b>Add credits to a number</b> above &mdash; it works before that
                number has verified.
              </p>
            )}

            {grants.length > 0 && (
              <div
                style={{
                  background: "#fff",
                  borderRadius: 8,
                  overflow: "auto",
                  border: "1px solid #e3e8ef",
                  boxShadow: "0 1px 3px rgba(16,42,73,0.08)",
                }}
              >
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      <th style={th}>When</th>
                      <th style={th}>Mobile</th>
                      <th style={th}>Name</th>
                      <th style={th}>Credits</th>
                      <th style={th}>Amount</th>
                      <th style={th}>Source</th>
                      <th style={th}>Given by</th>
                      <th style={{ ...th, whiteSpace: "normal" }}>Note</th>
                      <th style={th}>Used by this number</th>
                    </tr>
                  </thead>
                  <tbody>
                    {grants.map((g) => (
                      <tr key={g.SIRCreditId}>
                        <td style={td}>
                          {String(g.DateTime || "").replace("T", " ").slice(0, 16) || "-"}
                        </td>
                        <td style={{ ...td, fontWeight: 700 }}>{g.MobileNumber}</td>
                        <td style={td}>
                          {g.Name || <span style={{ color: "#b6c0cc" }}>not verified yet</span>}
                        </td>
                        <td style={{ ...td, fontWeight: 700, color: "#1b5e20" }}>
                          +{g.Credits}
                        </td>
                        <td style={td}>
                          {g.AmountPaid == null ? "-" : "\u20B9" + g.AmountPaid}
                        </td>
                        <td style={td}>{g.Source || "-"}</td>
                        <td style={td}>
                          {g.GrantedByName?.trim() || (g.GrantedBy ? "#" + g.GrantedBy : "-")}
                        </td>
                        <td style={{ ...td, whiteSpace: "normal" }}>{g.Note || "-"}</td>
                        <td style={td}>{g.UsedByNumber}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {/* ============ ANONYMOUS DEVICES ============
          * No name, no number — only a fingerprint, an IP and what they looked
          * up. That is enough to match the person on the phone ("you searched
          * for X a minute ago from this IP"), which is all this tab is for.
          */}
        {tab === "devices" && devices.length > 0 && (
          <div
            style={{
              background: "#fff",
              borderRadius: 8,
              overflow: "auto",
              border: "1px solid #e3e8ef",
              boxShadow: "0 1px 3px rgba(16,42,73,0.08)",
            }}
          >
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr>
                  <th style={th}>Device</th>
                  <th style={th}>IP</th>
                  <th style={th}>Platform</th>
                  <th style={th}>Free used</th>
                  <th style={th}>Left</th>
                  <th style={th}>Last seen</th>
                  <th style={{ ...th, whiteSpace: "normal" }}>Recent searches</th>
                </tr>
              </thead>
              <tbody>
                {devices.map((d, i) => {
                  const dry = d.FreeRemaining === 0;
                  return (
                    <tr
                      key={d.DeviceHash || i}
                      style={{
                        borderBottom: "1px solid #eef1f5",
                        background: dry ? "#fff8e1" : i % 2 ? "#fafbfd" : "#fff",
                      }}
                    >
                      <td style={{ ...td, fontFamily: "monospace" }} title={d.DeviceHash}>
                        {d.DeviceShort}…
                      </td>
                      <td style={{ ...td, fontWeight: 700 }}>{d.RawIp || "-"}</td>
                      <td style={{ ...td, whiteSpace: "normal", maxWidth: 220, overflow: "hidden" }}>
                        {d.Platform || "-"}
                      </td>
                      <td style={td}>
                        {d.FreeUsed} / {limits.freeLimit}
                      </td>
                      <td
                        style={{
                          ...td,
                          fontWeight: 800,
                          color: dry ? "#b3261e" : "#146c43",
                        }}
                      >
                        {dry ? "0 — exhausted" : d.FreeRemaining}
                      </td>
                      <td style={{ ...td, color: "#5a6672" }}>{d.LastSeen || "-"}</td>
                      <td
                        style={{
                          ...td,
                          whiteSpace: "normal",
                          color: "#5a6672",
                          fontSize: 11.5,
                        }}
                      >
                        {d.RecentQueries || "-"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div
        style={{
          borderTop: "1px solid #e3e8ef",
          padding: "4px 10px",
          fontSize: 11.5,
          color: "#5a6672",
          background: "#f7f9fc",
        }}
      >
        1 credit = 1 voter opened · credits never expire · every grant is recorded against your
        user id · an anonymous device must verify a mobile number before credits can reach it
      </div>
    </div>
  );
}
